import React, { useEffect, useState } from "react";
import { ChevronDown, Eye, EyeOff, HelpCircle, Globe } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../../components/footer/Footer";
import styles from "../../../styles/__login.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { postLogin } from "../../../reducer/login/loginReducer";
import axios from "axios";
import india from "../../../assets/images/flags/Flag_of_India-64x43.png";
import Illustration from "../../../assets/images/login-img-1.png";
import { RiEyeCloseLine, RiEyeLine } from "react-icons/ri";

const ImagingLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!username.trim()) {
      newErrors.username = "Username is required";
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[a-z]/.test(password)) {
      newErrors.password =
        "Password must include at least one lowercase letter";
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password =
        "Password must include at least one uppercase letter";
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Password must include at least one number";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      newErrors.password =
        "Password must include at least one special character";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const passwordRules = {
    length: {
      test: (pwd) => pwd.length >= 8,
      message: "At least 8 characters",
    },
    lowercase: {
      test: (pwd) => /[a-z]/.test(pwd),
      message: "At least one lowercase letter",
    },
    uppercase: {
      test: (pwd) => /[A-Z]/.test(pwd),
      message: "At least one uppercase letter",
    },
    number: {
      test: (pwd) => /[0-9]/.test(pwd),
      message: "At least one number",
    },
    special: {
      test: (pwd) => /[!@#$%^&*(),.?\":{}|<>]/.test(pwd),
      message: "At least one special character",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
    const loginData = {
      username: username,
      password: password,
      role_id: 10, // Hardcoded for Imaging role
    };

    const result = await dispatch(postLogin(loginData));
    if (result && result?.payload) {
      // Save tokens
      sessionStorage.setItem("usr_1a2b3c", result?.payload?.data?.access_token);
      sessionStorage.setItem(
        "usr_4d5e6f",
        result?.payload?.data?.refresh_token,
      );
      sessionStorage.setItem("usr_role", result?.payload?.data?.user?.role_id);
      // Store role_id for route protection
      if (result?.payload?.data?.user?.role_id) {
        sessionStorage.setItem(
          "usr_role_id",
          result?.payload?.data?.user?.role_id.toString(),
        );
      }
      sessionStorage.setItem("usr_name", result?.payload?.data?.user?.username);

      // Onboarding gate: an Imaging Center must have completed the onboarding
      // stepper (profile + at least one default address) before using the app.
      // Completion is detected by the presence of a default address; the result
      // is cached so route guards can enforce it without re-fetching.
      const accessToken = result?.payload?.data?.access_token;
      try {
        const addressRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/imaging/addresses`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        const addresses = addressRes?.data?.data || [];
        // Onboarding is complete once at least one default address exists.
        if (Array.isArray(addresses) && addresses.some((a) => a?.is_default)) {
          sessionStorage.setItem("usr_imaging_onboarded", "true");
          navigate("/imaging_dashboard");
        } else {
          sessionStorage.setItem("usr_imaging_onboarded", "false");
          navigate("/imaging_onboarding");
        }
      } catch (err) {
        // If the check fails, don't block login — send to the dashboard.
        sessionStorage.setItem("usr_imaging_onboarded", "true");
        navigate("/imaging_dashboard");
      }
    }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles["main-container"]}>
      {/* Main Content */}
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Doctor Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src={Illustration}
                alt="Imaging center illustration"
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Login Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("loginToAccount")}</h2>
            <form onSubmit={handleSubmit} className={styles["login-form"]}>
              {/* Username */}
              <div className={styles["form-group"]}>
                <label htmlFor="username">
                  Username <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  id="username"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={styles["form-input"]}
                  required
                />
                {errors?.username && (
                  <p className={styles["rule-invalid"]}>{errors?.username}</p>
                )}
              </div>

              {/* Password */}
              <div className={styles["form-group"]}>
                <label htmlFor="password">
                  {t("password")} <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <div className={styles["password-wrapper"]}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles["form-input"]}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles["password-toggle"]}
                  >
                    {showPassword ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeCloseLine size={20} />
                    )}
                  </button>
                </div>
                {/* <div className={styles["password-rules"]}>
                  {Object.keys(passwordRules).map((ruleKey) => {
                    const rule = passwordRules[ruleKey];
                    const isValid = rule.test(password);
                    return (
                      <p
                        key={ruleKey}
                        className={
                          isValid
                            ? styles["rule-valid"]
                            : styles["rule-invalid"]
                        }
                      >
                        {isValid ? "✔" : "✖"} {rule.message}
                      </p>
                    );
                  })}
                </div> */}
                <p className={styles["Forgot-pwd-link"]}>
                  <Link to="/forgot-password">{t("forgotPassword")}?</Link>
                </p>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className={styles["login-btn"]}
                disabled={loading}
                style={loading ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
              >
                {loading ? (
                  <span className={styles["btn-loading"]}>
                    <span className={styles["btn-spinner"]} />
                    {t("login")}
                  </span>
                ) : (
                  t("login")
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ImagingLogin;
